import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, monthLabel, errMsg } from "@/lib/api";
import { useMonth } from "@/context/MonthContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, ArrowRight, Users as UsersIcon, Plus, Trash2, Edit3, Download, Share2, Search } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#a855f7", "#06b6d4", "#f43f5e"];
const colorFor = (i) => COLORS[i % COLORS.length];

function downloadCSV(filename, rows) {
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

export default function Dagitim() {
  const { month } = useMonth();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "partner";

  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    company_id: "", amount: "", date: `${month}-01`, description: "",
  });
  const [preview, setPreview] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    const year = month.slice(0, 4); // yıl prefix'i; backend $regex ^2026 ile tüm yılı getirir
    const [c, d, p] = await Promise.all([
      api.get("/companies"),
      api.get("/distributions", { params: { month: year } }),
      api.get("/partners"),
    ]);
    setCompanies(c.data);
    setItems(d.data);
    setPartners(p.data);
  };
  useEffect(() => { load(); }, [month]);
  useEffect(() => { if (!editId) setForm((f) => ({ ...f, date: `${month}-01` })); }, [month, editId]);

  // Live preview: when company + amount change, show the auto-split based on company_shares
  useEffect(() => {
    const amt = Number(form.amount || 0);
    if (!form.company_id || !amt) { setPreview([]); return; }
    let cancelled = false;
    api.get(`/companies/${form.company_id}/shares`).then((r) => {
      if (cancelled) return;
      const shares = r.data || {};
      const rows = partners
        .filter((p) => shares[p.id] != null)
        .map((p, i) => ({
          partner_id: p.id,
          name: p.name,
          pct: Number(shares[p.id]),
          amount: +(amt * Number(shares[p.id]) / 100).toFixed(2),
          color: colorFor(partners.findIndex(x => x.id === p.id)),
        }));
      setPreview(rows);
    });
    return () => { cancelled = true; };
  }, [form.company_id, form.amount, partners]);

  const resetForm = () => {
    setEditId(null);
    setForm({ company_id: "", amount: "", date: `${month}-01`, description: "" });
    setPreview([]);
  };

  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (d) => {
    setEditId(d.id);
    setForm({
      company_id: d.company_id,
      amount: String(d.amount ?? ""),
      date: d.date,
      description: d.description || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editId) {
        await api.put(`/distributions/${editId}`, payload);
        toast.success("Dağıtım güncellendi");
      } else {
        await api.post("/distributions", payload);
        toast.success("Dağıtım kaydedildi — hissedarlara otomatik paylaştırıldı");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Dağıtım silinsin mi?")) return;
    try {
      await api.delete(`/distributions/${id}`);
      toast.success("Silindi");
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const filteredItems = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return items;
    return items.filter((d) =>
      companyName(d.company_id).toLocaleLowerCase("tr").includes(q) ||
      (d.description || "").toLocaleLowerCase("tr").includes(q) ||
      String(d.amount).includes(q) ||
      (d.date || "").includes(q) ||
      (d.splits || []).some((s) => (s.partner_name || "").toLocaleLowerCase("tr").includes(q))
    );
  }, [items, search, companies]);

  const totalDist = filteredItems.reduce((s, d) => s + Number(d.amount || 0), 0);

  // Per-partner summary (filtered)
  const partnerTotals = {};
  for (const d of filteredItems) {
    for (const s of d.splits || []) {
      partnerTotals[s.partner_id] = (partnerTotals[s.partner_id] || 0) + Number(s.amount || 0);
    }
  }

  const exportCSV = () => {
    const rows = [["Tarih", "Firma", "Tutar", "Hissedar", "Pay %", "Pay Tutarı", "Açıklama"]];
    for (const d of filteredItems) {
      if (!d.splits || d.splits.length === 0) {
        rows.push([d.date, companyName(d.company_id), d.amount, "", "", "", d.description || ""]);
      } else {
        for (const s of d.splits) {
          rows.push([d.date, companyName(d.company_id), d.amount, s.partner_name, s.percentage, s.amount, d.description || ""]);
        }
      }
    }
    downloadCSV(`dagitim-${month.slice(0, 4)}.csv`, rows);
    toast.success("CSV indirildi");
  };

  const previewTotal = preview.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="dagitim-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500 mb-1">Dağıtım</div>
          <h1 className="text-3xl font-bold tracking-tight fk-heading">
            Firma → Hissedar Dağıtım Kayıtları — {month.slice(0, 4)} Yılı
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Tarih, firma ve tutar girin; sistem firmanın hissedar oranlarına göre otomatik paylaştırır.
            Tüm yılın kayıtları aşağıda listelenir.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-dagitim"
                  className="bg-slate-900/60 border-slate-700 hover:bg-slate-800">
            <Download className="w-4 h-4 mr-1.5" /> CSV İndir
          </Button>
          {canEdit && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-dist-btn" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-500">
                  <Plus className="w-4 h-4 mr-1.5" /> Yeni Dağıtım
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#131b2e] border-white/10 max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editId ? "Dağıtımı Düzenle" : "Yeni Dağıtım Kaydı"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Tarih</Label>
                      <Input required type="date" data-testid="dist-date" value={form.date}
                             onChange={(e) => setForm({ ...form, date: e.target.value })}
                             className="bg-slate-900/60 border-slate-700" />
                    </div>
                    <div className="space-y-2">
                      <Label>Tutar (₺)</Label>
                      <Input required type="number" step="0.01" data-testid="dist-amount" value={form.amount}
                             onChange={(e) => setForm({ ...form, amount: e.target.value })}
                             className="bg-slate-900/60 border-slate-700" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Firma</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger data-testid="dist-company" className="bg-slate-900/60 border-slate-700">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Açıklama (opsiyonel)</Label>
                    <Input data-testid="dist-desc" value={form.description}
                           onChange={(e) => setForm({ ...form, description: e.target.value })}
                           className="bg-slate-900/60 border-slate-700" placeholder="örn. Şubat ayı kârı" />
                  </div>

                  {/* Preview */}
                  {preview.length > 0 && (
                    <div className="rounded-lg border border-indigo-500/25 bg-indigo-500/5 p-3" data-testid="dist-preview">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-300 mb-2 flex items-center gap-1.5">
                        <Share2 className="w-3 h-3" /> Otomatik Dağıtım Önizleme
                      </div>
                      <div className="space-y-1.5">
                        {preview.map((p) => (
                          <div key={p.partner_id} className="flex items-center gap-2 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.color }} />
                            <div className="flex-1 min-w-0 truncate text-white">{p.name}</div>
                            <div className="text-[10px] text-slate-500 fk-mono">%{p.pct.toFixed(2)}</div>
                            <div className="fk-mono text-sm font-medium text-indigo-300 w-28 text-right">{formatTRY(p.amount)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-indigo-500/20 flex items-center justify-between text-xs">
                        <span className="text-slate-400">Toplam</span>
                        <span className="fk-mono font-semibold text-white">{formatTRY(previewTotal)}</span>
                      </div>
                    </div>
                  )}

                  <DialogFooter>
                    <Button type="submit" data-testid="dist-submit" className="bg-indigo-600 hover:bg-indigo-500">
                      {editId ? "Güncelle" : "Dağıtımı Kaydet"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Dağıtım Sayısı</div>
          <div className="text-2xl font-semibold fk-mono mt-2">{filteredItems.length}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Toplam Dağıtılan</div>
          <div className="text-2xl font-semibold fk-mono mt-2 text-indigo-300">{formatTRY(totalDist)}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Dönem</div>
          <div className="text-2xl font-semibold fk-heading mt-2">{month.slice(0, 4)}</div>
        </div>
      </div>

      {/* Partner totals this month */}
      {Object.keys(partnerTotals).length > 0 && (
        <div className="fk-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon className="w-4 h-4 text-indigo-400" />
            <div className="text-sm font-semibold">Hissedar Yıl Toplamı</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {partners
              .filter((p) => partnerTotals[p.id])
              .sort((a, b) => (partnerTotals[b.id] || 0) - (partnerTotals[a.id] || 0))
              .map((p, i) => {
                const idx = partners.findIndex((x) => x.id === p.id);
                const color = colorFor(idx);
                return (
                  <div key={p.id} className="rounded-lg p-3 border bg-white/[.02]"
                       style={{ borderColor: `${color}33` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
                      <div className="text-xs text-slate-400 truncate">{p.name}</div>
                    </div>
                    <div className="fk-mono text-sm font-semibold" style={{ color }}>
                      {formatTRY(partnerTotals[p.id])}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Search + Distributions list */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <Input data-testid="dist-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                 placeholder="Ara: firma, hissedar, tarih, tutar…"
                 className="h-9 pl-9 bg-slate-900/60 border-slate-700" />
        </div>
      </div>

      <div className="space-y-3">
        {filteredItems.length === 0 ? (
          <div className="fk-card p-10 text-center text-slate-500">
            {search ? `"${search}" için dağıtım bulunamadı` : (items.length === 0 ? `Bu yıl için dağıtım kaydı yok. ${canEdit ? "Yeni Dağıtım ile başlayın." : ""}` : "")}
          </div>
        ) : (
          filteredItems.map((d) => (
            <div key={d.id} className="fk-card p-5" data-testid={`dist-${d.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-indigo-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white fk-heading truncate">{companyName(d.company_id)}</div>
                    <div className="text-xs text-slate-500 fk-mono mt-0.5">
                      {d.date}{d.description ? ` · ${d.description}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Toplam</div>
                    <div className="fk-mono text-lg font-semibold text-indigo-300">{formatTRY(d.amount)}</div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(d)}
                              data-testid={`edit-dist-${d.id}`}
                              className="text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10 h-8 w-8 p-0">
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(d.id)}
                              data-testid={`del-dist-${d.id}`}
                              className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {(d.splits || []).length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {d.splits.map((s) => {
                    const idx = partners.findIndex((x) => x.id === s.partner_id);
                    const color = idx >= 0 ? colorFor(idx) : "#64748b";
                    return (
                      <div key={s.partner_id} className="flex items-center gap-2 p-2 rounded-lg bg-white/[.02] border border-white/5">
                        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white truncate">{s.partner_name}</div>
                          <div className="text-[10px] text-slate-500 fk-mono">%{Number(s.percentage).toFixed(2)}</div>
                        </div>
                        <ArrowRight className="w-3 h-3 text-slate-600 shrink-0" />
                        <div className="fk-mono text-xs font-medium text-indigo-300 shrink-0">
                          {formatTRY(s.amount)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
